import { useTranslation } from "react-i18next";
import { WorkAgreementTemplateData } from "../../../../talents/talentAPI";
import { Document, Page, Text, View } from "@react-pdf/renderer";
import { formatDate, styles, yesNo } from "./helpers";

type Props = {
  workAgreement: WorkAgreementTemplateData;
};

export const ContinuousPDF = ({ workAgreement }: Props) => {
  const { t } = useTranslation();

  const salaryUnit = workAgreement.isHourlySalary
    ? t("common:hourlySalaryUnit")
    : t("common:monthlySalaryUnit");

  return workAgreement ? (
    <Document style={styles.document}>
      <Page size="A4">
        {/* VUOKRATYÖN LUONNE */}
        <View style={styles.section}>
          <Text style={styles.heading}>
            1.{" "}
            {t("workAgreementDocument:natureOfLeasedWork").toLocaleUpperCase()}
          </Text>
          <Text style={styles.paragraph}>
            {t("workAgreementDocument:natureOfLeasedWorkInfo")}
          </Text>
          <Text style={styles.paragraph}>
            {t("workAgreementDocument:natureOfLeasedWorkInfo2")}
          </Text>
          <Text>{t("workAgreementDocument:natureOfLeasedWorkInfo3")}</Text>
        </View>
        {/* TYÖSOPIMUKSEN OSAPUOLET */}
        <View style={styles.section}>
          <Text style={styles.heading}>
            2.{" "}
            {t(
              "workAgreementDocument:workAgreementParties",
            ).toLocaleUpperCase()}
          </Text>
          {/* TYÖNANTAJA */}
          <View style={styles.row}>
            <Text style={styles.bolded}>
              {t("workAgreementDocument:employer")}
              {": "}
            </Text>
            <Text>{workAgreement.employer.companyName}</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.bolded}>
              {t("invoices:businessId")}
              {": "}
            </Text>
            <Text>{workAgreement.employer.businessId}</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.bolded}>
              {t("common:street")}
              {": "}
            </Text>
            <Text>{workAgreement.employer.address.street}</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.bolded}>
              {t("workAgreementDocument:postCode")}
              {": "}
            </Text>
            <Text>{workAgreement.employer.address.zip}</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.bolded}>
              {t("workAgreementDocument:city/town")}
              {": "}
            </Text>
            <Text>{workAgreement.employer.address.city}</Text>
          </View>
          <View style={styles.divider} />
          {/* TYÖNTEKIJÄ */}
          <View style={styles.row}>
            <Text style={styles.bolded}>
              {t("workAgreementDocument:employee")}
              {": "}
            </Text>
            <Text>
              {workAgreement.employee.firstName}{" "}
              {workAgreement.employee.lastName}
            </Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.bolded}>
              {t("workAgreementDocument:ssn")}
              {": "}
            </Text>
            <Text>{workAgreement.employee.ssn}</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.bolded}>
              {t("common:street")}
              {": "}
            </Text>
            <Text>{workAgreement.employee.address.street}</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.bolded}>
              {t("workAgreementDocument:postCode")}
              {": "}
            </Text>
            <Text>{workAgreement.employee.address.zip}</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.bolded}>
              {t("workAgreementDocument:city/town")}
              {": "}
            </Text>
            <Text>{workAgreement.employee.address.city}</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.bolded}>
              {t("workAgreementDocument:telephone")}
              {": "}
            </Text>
            <Text>{workAgreement.employee.phone}</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.bolded}>
              {t("workAgreementDocument:email")}
              {": "}
            </Text>
            <Text>{workAgreement.employee.email}</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.bolded}>
              {t("workAgreementDocument:bankAccountNumber")}
              {": "}
            </Text>
            <Text>{workAgreement.employee.bankAccount}</Text>
          </View>
        </View>
        {/* TYÖTEHTÄVÄT JA TYÖNTEKOPAIKKA */}
        <View style={styles.section}>
          <Text style={styles.heading}>
            3.{" "}
            {t(
              "workAgreementDocument:workTitleAndLocation",
            ).toLocaleUpperCase()}
          </Text>
          <View style={styles.paragraph}>
            <Text>{t("workAgreementDocument:workTitleInfo")}</Text>
            <Text style={styles.bolded}>{workAgreement.workTitle}</Text>
          </View>
          <View style={styles.paragraph}>
            <Text>{t("workAgreementDocument:workInfo")}</Text>
          </View>
          <View style={styles.row}>
            <Text>
              {t("workAgreementDocument:workLocationInfo", {
                customerOrgName: workAgreement.customerOrgName,
                workLocationName: workAgreement.workLocation,
              })}
            </Text>
          </View>
        </View>
        {/* TYÖSOPIMUKSEN KESTO */}
        <View style={styles.section}>
          <Text style={styles.heading}>
            4.{" "}
            {t(
              "workAgreementDocument:workAgreementDuration",
            ).toLocaleUpperCase()}
          </Text>
          <View style={styles.paragraph}>
            <Text>{t("workAgreementDocument:continuousInfo")}</Text>
          </View>
          <View style={styles.paragraph}>
            <Text>{t("workAgreementDocument:workAgreementStartDate")}</Text>
            <Text style={styles.bolded}>
              {formatDate(workAgreement.startDate)}
            </Text>
          </View>
          <View style={styles.paragraph}>
            <Text>{t("workAgreementDocument:trialPeriodDecided")}</Text>
            <Text style={styles.bolded}>
              {t(yesNo(workAgreement.hasTrialPeriod))}
            </Text>
          </View>
          <View style={styles.paragraph}>
            <Text>{t("workAgreementDocument:trialPeriodEnds")}</Text>
            <Text style={styles.bolded}>
              {formatDate(workAgreement.trialPeriodEnds)}
            </Text>
          </View>
          <Text>{t("workAgreementDocument:trialPeriodInfo")}</Text>
        </View>
        {/* PALKKA JA TYÖEHTOSOPIMUS */}
        <View style={styles.section}>
          <Text style={styles.heading}>
            5.{" "}
            {t(
              "workAgreementDocument:salaryAndCollectiveAgreement",
            ).toLocaleUpperCase()}
          </Text>
          <View style={styles.paragraph}>
            <Text>
              {t("workAgreementDocument:collectiveAgreement")}
              <Text style={styles.bolded}>{workAgreement.tesName}</Text>
            </Text>
          </View>
          <View style={styles.paragraph}>
            <Text>{t("workAgreementDocument:collectiveAgreementInfo1")}</Text>
          </View>
          <View style={styles.paragraph}>
            <Text>{t("workAgreementDocument:salaryInfo1")}</Text>
          </View>
          <View style={styles.paragraph}>
            <Text>{t("workAgreementDocument:salaryInfo2")}</Text>
          </View>
          <View>
            <View style={styles.row}>
              {workAgreement.isHourlySalary ? (
                <Text>{t("workAgreementDocument:hourlySalaryAtTheStart")}</Text>
              ) : (
                <Text>{t("workAgreementDocument:salaryAtTheStart")}</Text>
              )}
              <Text style={styles.bolded}>
                {workAgreement.baseSalary} {salaryUnit}
              </Text>
            </View>
            {workAgreement.salaryComponents.map((component, i) => (
              <View key={i} style={styles.row}>
                <Text>{component.name}: </Text>
                <Text style={styles.bolded}>
                  {component.value} {salaryUnit}
                </Text>
              </View>
            ))}
            <View style={[styles.paragraph, { marginTop: 10 }]}>
              <Text>{t("workAgreementDocument:salaryOverall")}</Text>
              <Text style={styles.bolded}>
                {workAgreement.totalSalary} {salaryUnit}
              </Text>
            </View>
          </View>
          <Text>{t("workAgreementDocument:salaryInfo3")}</Text>
        </View>
        {/* TYÖAIKA JA YLITYÖT */}
        <View style={styles.section}>
          <Text style={styles.heading}>
            6.{" "}
            {t("workAgreementDocument:workTimeAndOvertime").toLocaleUpperCase()}
          </Text>
          <Text>{t("workAgreementDocument:regularWorkTimeLength")}</Text>
          <View style={styles.row}>
            <Text>{t("workAgreementDocument:fullTime")}</Text>
            <Text style={styles.bolded}>
              {t(yesNo(workAgreement.isFullTime))}
            </Text>
          </View>
          <View style={styles.row}>
            {workAgreement.isPeriodicWorkTime ? (
              <Text>{t("workAgreementDocument:periodicWorkTime")}</Text>
            ) : (
              <Text>{t("workAgreementDocument:workTimeOnAverage")}</Text>
            )}
            <Text style={styles.bolded}>
              {workAgreement.workTimeDescription}{" "}
              {t("workAgreementDocument:perWeek")}
            </Text>
          </View>
          <View style={styles.paragraph}>
            <Text>
              {t("workAgreementDocument:consentToOvertime")}
              <Text style={styles.bolded}>
                {t(yesNo(workAgreement.talentAgreesExtraWork))}
              </Text>
            </Text>
          </View>
          <Text>{t("workAgreementDocument:overtimeInfo")}</Text>
        </View>
        {/* VUOSILOMA */}
        <View style={styles.section}>
          <Text style={styles.heading}>
            7. {t("workAgreementDocument:annualLeave").toLocaleUpperCase()}
          </Text>
          <Text>{t("workAgreementDocument:annualLeaveInfo")}</Text>
        </View>
        {/* LIIKESALAISUUDET JA LOJAALIUSVELVOITE */}
        <View style={styles.section}>
          <Text style={styles.heading}>
            8.{" "}
            {t(
              "workAgreementDocument:businessSecretsAndDutyOfLoyalty",
            ).toLocaleUpperCase()}
          </Text>
          <Text style={styles.paragraph}>
            {t("workAgreementDocument:businessSecretsInfo")}
          </Text>
          <Text>{t("workAgreementDocument:businessSecretsInfo2")}</Text>
        </View>
        {/* MUUT EHDOT */}
        <View style={styles.section}>
          <Text style={styles.heading}>
            9. {t("workAgreementDocument:otherTerms").toLocaleUpperCase()}
          </Text>
          <Text style={styles.paragraph}>
            {t("workAgreementDocument:otherTermsInfo")}
          </Text>
          <Text style={styles.paragraph}>
            {t("workAgreementDocument:otherTermsInfo2")}
          </Text>
          <Text style={styles.paragraph}>
            {t("workAgreementDocument:otherTermsInfo3")}
          </Text>
          <Text style={styles.paragraph}>
            {t("workAgreementDocument:otherTermsInfo4")}
          </Text>
          <Text style={styles.paragraph}>
            {t("workAgreementDocument:otherTermsInfo10")}
            {t("workAgreementDocument:otherTermsInfo6")}
          </Text>
          <Text style={styles.paragraph}>
            {t("workAgreementDocument:otherTermsInfo7")}
          </Text>
          <Text style={styles.paragraph}>
            {t("workAgreementDocument:otherTermsInfoContinuous")}
          </Text>
        </View>
        {/* MUUTA SOVITTU */}
        <View style={styles.section}>
          <Text style={styles.heading}>
            10. {t("workAgreementDocument:otherAgreed").toLocaleUpperCase()}
          </Text>
          <Text>{workAgreement.extraInformationAgreed}</Text>
        </View>
        {/* ALLEKIRJOITUKSET */}
        <View style={styles.section}>
          <Text style={styles.heading}>
            11. {t("workAgreementDocument:signatures").toLocaleUpperCase()}
          </Text>
          {workAgreement.isAccepted ? (
            <Text style={styles.paragraph}>
              {t("workAgreementDocument:signedElectronically", {
                date:
                  workAgreement.acceptedAt &&
                  formatDate(workAgreement.acceptedAt),
              })}
            </Text>
          ) : (
            <View style={styles.divider} />
          )}
          <View style={[styles.grid, styles.signature, styles.paragraph]}>
            <View style={styles.padded}>
              <Text>{t("workAgreementDocument:employerSignature")}</Text>
              <Text>
                {workAgreement.employerContact.firstName}{" "}
                {workAgreement.employerContact.lastName}
              </Text>
            </View>
            <View style={styles.padded}>
              <Text>{t("workAgreementDocument:employeeSignature")}</Text>
              <Text>
                {workAgreement.employee.firstName}{" "}
                {workAgreement.employee.lastName}
              </Text>
            </View>
          </View>
          <Text>{t("workAgreementDocument:generalCollectiveAgreements")}</Text>
        </View>
      </Page>
    </Document>
  ) : null;
};
